import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {format, addHours} from 'ag-common/facades'
import i18n from 'i18next'
import {connect} from 'react-redux'
import actions from 'redux-auto'
import DateRangePicker from '#components/m/DateRangePicker'
import Api from '#services/api'
import HintBox from '../HintBox'
import UserSelectionInput from './UserSelectionInput'
import DialogControlButtons from './DialogControlButtons'
import AbteilungSelect from './AbteilungSelect'
import DialogDeleteIcon from './DialogDeleteIcon'
import Loading from '#components/a/Loading'

export const AbteilungDialog = ({
  userId,
  azubis,
  closed,
  id,
  from,
  to,
  abteilung,
  versetzungsplaene,
  createAbteilung,
  editAbteilung,
  deleteAbteilung,
}) => {
  const [isInTimeRangeOfOtherEntry, setIsInTimeRangeOfOtherEntry] = useState(false)
  const [hasUeberplanungsConflict, setHasUeberplanungsConflict] = useState(false)
  const [conflictCalculationLoading, setConflictCalculationLoading] = useState(false)
  const [abteilungId, setAbteilungId] = useState(abteilung ? abteilung.id : undefined)
  const [datePickerStart, setDatePickerStart] = useState(from ? from : new Date())
  const [datePickerEnd, setDatePickerEnd] = useState(to ? to : new Date())
  const [azubiId, setAzubiId] = useState(userId)
  const isCreateDialog = !userId

  useEffect(() => {
    const abortController = new AbortController()
    ;(async () => {
      setIsInTimeRangeOfOtherEntry(false)
      setHasUeberplanungsConflict(false)
      setConflictCalculationLoading(true)
      try {
        const {hasAbteilungConflict, hasRessourcenConflict} = await Api.calculateConflictsForAzubi(
          azubiId,
          abteilungId,
          id,
          datePickerStart,
          datePickerEnd,
          abortController.signal,
        )
        setIsInTimeRangeOfOtherEntry(hasAbteilungConflict)
        setHasUeberplanungsConflict(hasRessourcenConflict)
      } catch (e) {
        if (e.name !== 'AbortError') {
          throw e
        }
      }
      setConflictCalculationLoading(false)
    })()
    return () => abortController.abort()
  }, [azubiId, abteilungId, id, datePickerStart, datePickerEnd])

  useEffect(() => {
    if (!azubiId && azubis && azubis.length > 0) {
      setAzubiId(azubis[0]?.id)
    }
  }, [azubiId, azubis])

  useEffect(() => {
    if (userId) {
      setAzubiId(userId)
    }
  }, [userId])

  useEffect(() => {
    if (abteilung) {
      setAbteilungId(abteilung.id)
    }
  }, [abteilung?.id])

  useEffect(() => {
    setDatePickerStart(from ? from : new Date())
  }, [from])

  useEffect(() => {
    setDatePickerEnd(to ? to : new Date())
  }, [to])

  const onDateRangeChanged = (start, end) => {
    setDatePickerStart(start)
    setDatePickerEnd(end)
  }

  const handleSubmit = event => {
    event.preventDefault()
    const abteilungCreationModel = {
      id,
      abteilungId,
      userId: azubiId,
      from: format(datePickerStart, 'yyyy-MM-dd'),
      to: format(datePickerEnd, 'yyyy-MM-dd'),
    }

    if (isCreateDialog) {
      createAbteilung(abteilungCreationModel)
    } else {
      editAbteilung(abteilungCreationModel)
    }

    closed()
  }

  const onAzubiChanged = azubiId => {
    setAzubiId(azubiId)
  }

  const onDeleteAbteilung = () => {
    deleteAbteilung({abteilungId: id, userId: userId})
    closed()
  }

  const onAbteilungChanged = abteilungId => {
    setAbteilungId(abteilungId)
  }

  const closeDialog = evt => {
    evt.stopPropagation()
    closed()
  }

  const className = isCreateDialog ? 'abteilungCreateDialog' : 'abteilungEditDialog'
  let selectedAzubi

  if (!isCreateDialog) {
    selectedAzubi = azubis.find(user => user.id === userId)
  }

  return (
    <form onSubmit={handleSubmit} className={`abteilungDialog ${className}`}>
      {!isCreateDialog && (
        <>
          <DialogDeleteIcon id={id} onConfirm={onDeleteAbteilung} type="abteilung" />
          <div className="user">{`${selectedAzubi.lastname}, ${selectedAzubi.firstname} `}</div>
        </>
      )}
      <UserSelectionInput
        azubis={azubis}
        selectedAzubiId={azubiId}
        onSelectAzubi={onAzubiChanged}
        isDisabled={!isCreateDialog}
      />
      <DateRangePicker
        initialStartDate={datePickerStart}
        initialEndDate={datePickerEnd}
        onRangeUpdated={onDateRangeChanged}
      />
      <AbteilungSelect
        versetzungsplaene={versetzungsplaene}
        selectedAbteilungId={abteilungId}
        onSelectAbteilung={onAbteilungChanged}
        azubiId={azubiId}
      />
      {isInTimeRangeOfOtherEntry && (
        <HintBox modifier="warning" text={i18n.t('versetzungsplanAbteilung_overlap_conflict_hint')} />
      )}
      {hasUeberplanungsConflict && (
        <HintBox
          modifier="warning"
          dataTestId="ueberbucht_conflict_hint"
          text={i18n.t('versetzungsplanAbteilung_ueberbucht_conflict_hint')}
        />
      )}
      {conflictCalculationLoading && <Loading />}
      <DialogControlButtons onCloseClick={closeDialog} onSubmitClick={handleSubmit} />
    </form>
  )
}

// eslint-disable-next-line max-len
export default connect(mapDispatchToProps)(AbteilungDialog)

function mapDispatchToProps() {
  return {
    createAbteilung: actions.versetzungsplan.createAbteilung,
    editAbteilung: actions.versetzungsplan.editAbteilung,
    deleteAbteilung: actions.versetzungsplan.deleteAbteilung,
  }
}

AbteilungDialog.propTypes = {
  azubis: PropTypes.array.isRequired,
  userId: PropTypes.number,
  closed: PropTypes.func,
  id: PropTypes.string,
  from: PropTypes.instanceOf(Date),
  to: PropTypes.instanceOf(Date),
  abteilung: PropTypes.shape({
    id: PropTypes.number,
  }),
  versetzungsplaene: PropTypes.array,
  createAbteilung: PropTypes.func,
  editAbteilung: PropTypes.func,
  deleteAbteilung: PropTypes.func,
  readVersetzungsplan: PropTypes.func,
}
